import { EnvironmentProviders, Injectable, inject, signal } from '@angular/core';
import { buildStepToWorkflowMap, provideWorkflowTracking, TrackWorkflowService } from '@aiwyn/shared';
import { UserType } from '@aiwyn/auth';
import { PaymentFlow, PaymentMethodIntent } from '../../platforms/models/payment-method-metadata.models';
import {
  PaymentMethodEventBaseMetadata,
  PaymentMethodEventEventTypeMap,
  PaymentMethodStepMetadataInput,
  PaymentMethodSurface,
  PaymentMethodWorkflowEvent,
  PaymentMethodWorkflowEventMetaDataMap,
} from './payment-method-workflow.models';
import { PaymentsWorkflowType } from '../payments-workflow.models';

/**
 * Per-container tracker for Flow 7 (Managing Payment Methods). Owns the
 * `PaymentMethodEventBaseMetadata` envelope as signal-driven fields populated by the host
 * container's `set*` calls, and merges them into per-call metadata at dispatch — call sites
 * pass only event-specific fields. Exposes `startOperation` / `endOperation` so the SUBMITTED →
 * SUCCEEDED / FAILED triads can stamp `operationDurationMs`.
 *
 * Per-container scope (`@Injectable()`, no `providedIn`). Wire via
 * `providers: [PaymentMethodTrackService]` on the host container so concurrently-mounted hosts
 * (e.g. settings + an open dialog) don't share envelope or operation timers — the previous
 * stack-based context service is replaced by this scoping.
 */
@Injectable()
export class PaymentMethodTrackService {
  private readonly tracker = inject(TrackWorkflowService) as TrackWorkflowService<PaymentMethodWorkflowEvent, PaymentMethodWorkflowEventMetaDataMap>;
  private readonly operationStarts = new Map<string, number>();

  private readonly _surface = signal<PaymentMethodSurface | undefined>(undefined);
  private readonly _intent = signal<PaymentMethodIntent | undefined>(undefined);
  private readonly _paymentFlow = signal<PaymentFlow | undefined>(undefined);
  private readonly _userType = signal<UserType | undefined>(undefined);
  private readonly _isFirstPaymentMethod = signal<boolean | undefined>(undefined);

  setSurface(surface: PaymentMethodSurface): void {
    this._surface.set(surface);
  }

  setIntent(intent: PaymentMethodIntent | undefined): void {
    this._intent.set(intent);
  }

  setPaymentFlow(paymentFlow: PaymentFlow | undefined): void {
    this._paymentFlow.set(paymentFlow);
  }

  setUserType(userType: UserType): void {
    this._userType.set(userType);
  }

  setIsFirstPaymentMethod(isFirstPaymentMethod: boolean | undefined): void {
    this._isFirstPaymentMethod.set(isFirstPaymentMethod);
  }

  trackWorkflow<E extends PaymentMethodWorkflowEvent>(step: E, metadata?: PaymentMethodStepMetadataInput<E>): void {
    const merged = { ...this.resolveBaseMetadata(), ...(metadata ?? {}) } as PaymentMethodWorkflowEventMetaDataMap[E];
    this.tracker.trackWorkflow(step, merged);
  }

  startOperation(key: string): void {
    this.operationStarts.set(key, performance.now());
  }

  endOperation(key: string): number {
    const start = this.operationStarts.get(key);
    if (start === undefined) {
      return 0;
    }
    this.operationStarts.delete(key);
    return performance.now() - start;
  }

  private resolveBaseMetadata(): PaymentMethodEventBaseMetadata {
    return {
      surface: this._surface(),
      intent: this._intent(),
      paymentFlow: this._paymentFlow(),
      userType: this._userType(),
      isFirstPaymentMethod: this._isFirstPaymentMethod(),
    };
  }
}

export function providePaymentMethodWorkflowTracking(): EnvironmentProviders {
  return provideWorkflowTracking<PaymentMethodWorkflowEvent, PaymentMethodWorkflowEventMetaDataMap>({
    stepToWorkflow: buildStepToWorkflowMap({ [PaymentsWorkflowType.PAYMENT_METHOD]: PaymentMethodWorkflowEvent }),
    stepToEventType: PaymentMethodEventEventTypeMap,
  });
}
